import Phaser from 'phaser';
import levelData from '@/Levels/JSON/level1.json';
import { MovingPlatform, BounceFlower, PressureSwitch, MechanicalDoor, BreakableBlock } from '../Entities/InteractiveObjects';

export interface LevelDataStructure {
  name: string;
  width: number;
  height: number;
  tileSize: number;
  spawn: { x: number; y: number };
  platforms: { x: number; y: number; width: number; height: number; type: string }[];
  movingPlatforms: { x: number; y: number; targetX: number; targetY: number; speed: number }[];
  hazards: { x: number; y: number; width: number; height: number; type: string }[];
  checkpoints: { id: string; x: number; y: number }[];
  portals: { id: string; targetId: string; x: number; y: number; type: 'portal_blue' | 'portal_purple' }[];
  secretRoom: {
    bounds: { x: number; y: number; width: number; height: number };
    platforms: { x: number; y: number; width: number; height: number; type: string }[];
    gems: { x: number; y: number }[];
  };
  interactiveObjects: {
    bounceFlowers: { x: number; y: number; boostPower: number }[];
    vines: { x: number; y: number; length: number }[];
    switches: { id: string; x: number; y: number; doorId: string }[];
    doors: { id: string; x: number; y: number }[];
    windTunnels: { x: number; y: number; width: number; height: number; forceY: number }[];
  };
  enemies: { type: 'ground' | 'flying' | 'drone' | 'golem'; x: number; y: number; patrolDistance: number }[];
  collectibles: {
    coins: { x: number; y: number }[];
    gems: { x: number; y: number }[];
  };
  npcs: { id: string; x: number; y: number; dialogue: string }[];
  castle: { x: number; y: number };
}

export class LevelManager {
  private scene: Phaser.Scene;
  public staticPlatforms: Phaser.Physics.Arcade.StaticGroup;
  public hazardGroup: Phaser.Physics.Arcade.StaticGroup;
  public movingPlatforms: MovingPlatform[] = [];
  public bounceFlowers: BounceFlower[] = [];
  public switches: PressureSwitch[] = [];
  public doors: Map<string, MechanicalDoor> = new Map();
  public breakableBlocks: BreakableBlock[] = [];
  public vinesGroup: Phaser.Physics.Arcade.StaticGroup;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
    this.staticPlatforms = scene.physics.add.staticGroup();
    this.hazardGroup = scene.physics.add.staticGroup();
    this.vinesGroup = scene.physics.add.staticGroup();
  }

  public loadLevel(customData?: LevelDataStructure): LevelDataStructure {
    const data = (customData || levelData) as unknown as LevelDataStructure;

    // Build Static Platforms
    data.platforms.forEach((p) => {
      this.createTiledPlatform(p.x, p.y, p.width, p.height, p.type);
    });

    // Build Moving Platforms
    data.movingPlatforms.forEach((mp) => {
      const platform = new MovingPlatform(this.scene, mp.x, mp.y, mp.targetX, mp.targetY, mp.speed);
      platform.setDepth(10);
      this.movingPlatforms.push(platform);
    });

    // Build Hazards (Lava, Spikes, Water)
    data.hazards.forEach((h) => {
      let textureKey = 'tile_lava';
      if (h.type === 'spikes') textureKey = 'tile_breakable';
      const hazard = this.hazardGroup.create(h.x + h.width / 2, h.y + h.height / 2, textureKey) as Phaser.Physics.Arcade.Sprite;
      hazard.setDisplaySize(h.width, h.height);
      hazard.setDepth(12);
      hazard.refreshBody();
    });

    // Build Secret Room Platforms & Gems
    if (data.secretRoom) {
      data.secretRoom.platforms.forEach((p) => {
        this.createTiledPlatform(p.x, p.y, p.width, p.height, p.type);
      });
      data.secretRoom.gems.forEach((g) => {
        data.collectibles.gems.push(g);
      });
    }

    // Build Bounce Flowers
    data.interactiveObjects.bounceFlowers.forEach((bf) => {
      const flower = new BounceFlower(this.scene, bf.x, bf.y, bf.boostPower);
      flower.setDepth(15);
      this.bounceFlowers.push(flower);
    });

    // Build Vines
    data.interactiveObjects.vines.forEach((v) => {
      for (let i = 0; i < v.length; i++) {
        const vineTile = this.vinesGroup.create(v.x, v.y + i * 32, 'vine') as Phaser.Physics.Arcade.Sprite;
        vineTile.setDepth(15);
        vineTile.refreshBody();
      }
    });

    // Build Switches & Doors
    data.interactiveObjects.doors.forEach((d) => {
      const door = new MechanicalDoor(this.scene, d.x, d.y, d.id);
      door.setDepth(15);
      this.doors.set(d.id, door);
    });

    data.interactiveObjects.switches.forEach((sw) => {
      const pSwitch = new PressureSwitch(this.scene, sw.x, sw.y, sw.id, sw.doorId);
      pSwitch.setDepth(15);
      this.switches.push(pSwitch);
    });

    return data;
  }

  private createTiledPlatform(x: number, y: number, width: number, height: number, textureKey: string): void {
    const tileSize = 32;
    for (let curX = x; curX < x + width; curX += tileSize) {
      for (let curY = y; curY < y + height; curY += tileSize) {
        const tile = this.staticPlatforms.create(curX + tileSize / 2, curY + tileSize / 2, textureKey) as Phaser.Physics.Arcade.Sprite;
        tile.setDepth(10);
        tile.refreshBody();
      }
    }
  }

  public updateLevel(delta: number): void {
    this.movingPlatforms.forEach((mp) => mp.updatePlatform(delta));
  }
}
